// TODO: Replace all these types with shared types if/when we factor API types into a codebase shared by FE/BE

import { MapCoordinates } from '@/components/Map';

export type GiftType = 'physical' | 'digital';

export type CreatePostDTO = {
    title: string;
    body: string;
    tags: string[];
    type: string;
    giftType?: GiftType;
    files?: File[];
    categoryID?: number | null;
    location?: LocationDTO | null;
    itemsLimit?: number | null;
};

export type UpdatePostDTO = Omit<Partial<PostDTO>, 'tags'> & {
    tags: string[];
};

export type LocationDTO = {
    id?: number;
    name?: string | null;
    regionID: string | null;
    global?: boolean;
    latitude?: number | null;
    longitude?: number | null;
    // Fuzzed coordinates for privacy (approximate location display)
    fuzzedLatitude?: number | null;
    fuzzedLongitude?: number | null;
};

export type PostDTO = {
    id: number;
    sender?: UserDTO;
    senderID: number;
    title: string;
    body: string;
    isPast?: boolean;
    images?: string[];
    type: 'request' | 'gift';
    giftType?: GiftType;
    status: string;
    isActive: boolean;
    kudos?: number;
    createdAt: Date;
    updatedAt: Date;
    location: LocationDTO;
    category: CategoryDTO;
    itemsLimit?: number | null;
    handshakes?: HandshakeDTO[];
    rewardOffers?: RewardOfferDTO[];
    messages?: MessageDTO[];
    tags?: Tag[];
    distance?: number | null;
};

export interface CustomFile extends File {
    uri: string;
}

export type ProfileFormValues = {
    about?: string;
    profession?: string;
    username?: string;
    displayName?: string;
    email: string;
    avatar: File[];
    avatarURL?: string;
    location: MapCoordinates;
    tags?: string[];
    admin?: boolean;
    mapCoordinates?: {
        latitude: number;
        longitude: number;
    };
    kudos?: number;
};

export interface Feat {
    location: string;
    date: Date;
    placement: number;
    description: string;
}

export type UserLoginRequestSchemaDTO = {
    username?: string;
    password?: string;
    token?: string;
};

export type UserLoginResponseDTO = {
    token: string;
    user: {
        username: string;
    };
};

export type CreateUserDTO = {
    username: string;
    email: string;
    password: string;
};

export interface MessageDTO {
    id: number;
    authorID: number;
    postID?: number;
    eventID?: number;
    channelID?: number;
    replyToMessageID?: number;
    handshakeID?: number;
    content: string;
    createdAt: Date;
    updatedAt: Date;
    readAt?: Date;
    deletedAt?: Date | string | null;
    //TODO: Stuff is missing
    author?: UserDTO;
}

export type CreateMessageDTO = Partial<MessageDTO>;

export type UpdateMessageDTO = Partial<Pick<MessageDTO, 'content'>>;

export interface SendCommentDTO {
    content: string;
}

export type CreateRewardOfferDTO = {
    postID: number;
    kudos: number;
    currency: string;
    receiverID?: number;
    senderID?: number;
};

export type RewardOfferDTO = {
    id: number;
    postId: number;
    senderId: number;
    amount: number;
    kudosFinal: number;
    createdAt: Date;
    updatedAt: Date;
};

export type CreateHandshakeDTO = {
    postID: number;
    senderID: number;
    receiverID: number;
    type: string;
    status: string;
};

export type HandshakeDTO = {
    receiverID: any;
    id: number;
    postID: number;
    offerID: number;
    senderID: number;
    recipientID: number;
    sender?: UserDTO;
    status: string;
    createdAt: Date;
    updatedAt: Date;
    noShowReported?: boolean | null;
    cancelledByUserID?: number | null;
    cancelledAt?: string | null;
    post: PostDTO;
};

export type ChannelDTO = {
    id: number;
    name: string;
    type: 'public' | 'dm' | 'group' | 'post' | 'community';
    createdAt: Date;
    updatedAt: Date;
    users?: any[]; // Array of users in the channel
    otherUser?: any; // The other user in a DM channel
    lastMessage?: any; // The last message in the channel
};

export type EventDTO = {
    id: number;
    title: string;
    description: string;
    isGlobal: boolean;
    link?: string | null;
    locationID: number | null;
    startTime: string;
    endTime: string;
    creatorID: number | null;
    creator?: UserDTO | null;
    createdAt: string;
    updatedAt: string;
    content?: string;
    participants?: UserDTO[];
    location?: LocationDTO | null;
    participantCount?: number;
    messages?: MessageDTO[];
};

export type EventRecurrenceDTO = {
    frequency: 'daily' | 'weekly' | 'monthly';
    occurrences?: number;
};

export type CreateEventDTO = {
    title: string;
    description: string;
    link?: string | null;
    locationID?: string | null;
    startTime: Date;
    endTime?: Date | null;
    content?: string;
    location: LocationDTO | null;
    recurrence?: EventRecurrenceDTO;
};

export interface UpdateEventDTO {
    title: string;
    description: string;
    startTime: Date;
    endTime?: Date | null;
    location?: {
        regionID?: string | null;
        name?: string;
        global?: boolean;
    } | null;
}

export type CategoryDTO = {
    id: number;
    name: string;
    createdAt: Date;
    updatedAt: Date;
};

export interface UserTagDTO {
    name: string;
    description?: string;
    userCount?: number;
    postCount?: number;
    users?: string[];
    posts?: string[];
}

export interface UserTagRequestDTO {
    name: string;
    description?: string;
}

export interface UserSettingsDTO {
    userID: number;
    skills: string[];
    about: string;
    profession?: string | null;
    emailNotificationsEnabled: boolean;
    tags: string[];
    blockedUsers: number[];
    invitationToken: string;
    invitationCreatedAt: string;
    invitationSentAt: string;
    invitationAcceptedAt: string;
    invitationLimit: number;
    invitedByType: string;
    invitedByID: number;
    invitationsCount: number;
    id: number;
    createdAt: string;
    updatedAt: string;
}

export interface TopTagDTO {
    id: string;
    name: string;
    count: number;
}

export interface Post {
    id: string;
    sender: UserDTO;
    rewardOffer: {
        kudos: number;
        status: string;
        body: string;
        kudosFinal: number;
        createdAt: Date;
        updatedAt: Date;
    } | null;
    tags: Tag[];
    location: LocationDTO;
    title: string;
    type: 'request' | 'gift';
    giftType?: GiftType;
    body: string;
    createdAt: Date;
    updatedAt: Date;
    deletedAt: Date;
    status: string;
    kudos: number; // TODO: Get this from post
    rewardOffers: any[]; // TODO
    messages: any[]; // TODO
    // location: any; // TODO
    handshakes: any[]; // TODO
    images: string[]; // TODO
    category: CategoryDTO;
}

interface Badge {
    id: number;
    name: string;
    image: string;
}

export interface Tag {
    id: number;
    name: string;
}

export interface UserDTO {
    id: number;
    discordID: string | null;
    googleID: string | null;
    displayName?: string | null;
    username: string;
    email: string;
    avatar?: string | null;
    admin: boolean;
    kudos: number;
    invitedByUserID?: number | null;
    isEmailVerified?: boolean;
    password?: string | null;
    locationID: number | null;
    tags: Tag[];
    badges: Badge[];
    location?: MapCoordinates;
    settings?: UserSettingsDTO | null;
    createdAt: Date;
    updatedAt: Date;
    deactivatedAt?: Date | null;
    privacyErasedAt?: Date | null;
}

export interface UserInviteDTO {
    id: number;
    createdByUserID: number;
    targetEmail?: string | null;
    usedByUserID?: number | null;
    usedAt?: string | null;
    revoked: boolean;
    inviteUrl?: string | null;
    usedBy?: UserDTO | null;
    createdAt: string;
    updatedAt: string;
}

export interface UserInviteListResponse {
    invites: UserInviteDTO[];
    nextCursor: number | null;
}

export type FeedbackKind = 'site-feedback' | 'bug-report';
export type FeedbackStatus = 'new' | 'archived' | 'resolved';

export interface FeedbackDTO {
    id: number;
    userID: number;
    user?: UserDTO;
    title: string;
    description: string;
    type: FeedbackKind;
    category: string;
    tags: string[] | null;
    attachments: string[] | null;
    status: FeedbackStatus;
    rewardKudos?: number | null;
    baseRewardKudos: number;
    createdAt: string;
    updatedAt: string;
}

export const NotificationType = {
    DIRECT_MESSAGE: 'direct-message',
    MESSAGE_REPLY: 'message-reply',
    POST_REPLY: 'post-reply',
    EVENT_REPLY: 'event-reply',
    POST_AUTO_CLOSE: 'post-auto-close',
    PAST_GIFT: 'past-gift',
    BUG_REPORT: 'bug-report',
    SITE_FEEDBACK: 'site-feedback',
    USER_BANNED: 'user-banned',
    HANDSHAKE_CREATED: 'handshake-created',
    HANDSHAKE_ACCEPTED: 'handshake-accepted',
    HANDSHAKE_UNDO_ACCEPTED: 'handshake-undo-accepted',
    HANDSHAKE_COMPLETED: 'handshake-completed',
    HANDSHAKE_CANCELLED: 'handshake-cancelled',
    POST_CLOSED_BY_OTHER_HANDSHAKE: 'post-closed-by-other-handshake',
    POST_REOPENED: 'post-reopened',
    EVENT_USER_JOINED: 'event-user-joined',
    EVENT_INVITE: 'event-invite',
    KUDOS_RECEIVED: 'kudos-received'
} as const;

export type NotificationTypeKeys =
    (typeof NotificationType)[keyof typeof NotificationType];

export type DirectMessageNotification = {
    type: typeof NotificationType.DIRECT_MESSAGE;
    channelID: number;
    message: MessageDTO;
};

export type PostReplyNotification = {
    type: typeof NotificationType.POST_REPLY;
    postID: number;
    message: MessageDTO;
};

export type MessageReplyNotification = {
    type: typeof NotificationType.MESSAGE_REPLY;
    message: MessageDTO;
    channelID?: number;
    channelType?: ChannelDTO['type'];
    postID?: number;
    eventID?: number;
};

export type EventReplyNotification = {
    type: typeof NotificationType.EVENT_REPLY;
    eventID: number;
    message: MessageDTO;
};

export type EventUserJoinedNotification = {
    type: typeof NotificationType.EVENT_USER_JOINED;
    eventID: number;
    userID: number;
    user?: UserDTO;
};

export type EventInviteNotification = {
    type: typeof NotificationType.EVENT_INVITE;
    eventID: number;
    userID: number;
    user?: UserDTO;
};

export type NotificationPayload =
    | DirectMessageNotification
    | MessageReplyNotification
    | PostReplyNotification
    | EventReplyNotification
    | EventUserJoinedNotification
    | EventInviteNotification
    | {
          type: typeof NotificationType.POST_AUTO_CLOSE;
          postID: number;
          closeAt?: string;
          closedAt?: string;
      }
    | { type: typeof NotificationType.PAST_GIFT; postID: number }
    | { type: typeof NotificationType.BUG_REPORT; feedbackID: number }
    | { type: typeof NotificationType.SITE_FEEDBACK; feedbackID: number }
    | {
          type: typeof NotificationType.HANDSHAKE_CREATED;
          postID: number;
          handshakeID: number;
      }
    | {
          type: typeof NotificationType.HANDSHAKE_ACCEPTED;
          postID: number;
          handshakeID: number;
      }
    | {
          type: typeof NotificationType.HANDSHAKE_UNDO_ACCEPTED;
          postID: number;
          handshakeID: number;
      }
    | {
          type: typeof NotificationType.HANDSHAKE_COMPLETED;
          postID: number;
          handshakeID: number;
      }
    | {
          type: typeof NotificationType.HANDSHAKE_CANCELLED;
          postID: number;
          handshakeID: number;
          noShowReported?: boolean;
      }
    | {
          type: typeof NotificationType.POST_CLOSED_BY_OTHER_HANDSHAKE;
          postID: number;
          handshakeID: number;
      }
    | {
          type: typeof NotificationType.POST_REOPENED;
          postID: number;
          handshakeID: number;
      }
    | {
          type: typeof NotificationType.KUDOS_RECEIVED;
          postID?: number;
          feedbackID?: number;
          kudos?: number;
      };

export type NotificationRecord = NotificationPayload & {
    id: number;
    isRead: boolean;
    isActedOn: boolean;
    createdAt?: string;
};

export type NotificationsHistoryResponse = {
    items: NotificationRecord[];
    nextCursor?: number;
};
